import { add, copy, now, today, tomorrow } from "./datetime"
import { STATE_ENUM } from "./consts"

/**
 * Inject values in `document.template` object
 * @param {*} template The `document.template` object to inject values in
 * @param {Array} fieldvalues The list of `document.fieldValues` to inject
 * @returns The document object with values
 */
function injectValues(template, fieldvalues) {
  let document = Object.assign({}, template)
  for (const section of document.sections) {
    for (const field of section.fields) {
      field.values = fieldvalues.find(e => e.field === field.id)?.values
    }
  }
  return document
}

/**
 * MAKE/FACTORY METHODS
 **/

/**
 * Create new empty `Document` object
 * @returns The new Document
 */
function mkDocument() {
  return {
    name: "",
    state: STATE_ENUM.EDITABLE,
    project: null
  }
}

/**
 * Create new empty `Inspection` object
 * @returns The new Inspection
 */
function mkInspection() {
  return {
    end_date: tomorrow(),
    name: "",
    notify_oncreated: false,
    notify_team_oncreated: false,
    start_date: now(),
    status: "Ongoing",
    users: [],
    external_emails: [],
    lang: "",
    attachments: []
  }
}

/**
 * Create new empty `PcqDsi` object
 * @returns The new PcqDsi
 */
function mkPcqDsi() {
  return {
    task: null,
    name: "",
    pia: "",
    applicable_document: "",
    doc_rev: "",
    performer: null,
    quality_control: null,
    technical_control: null,
    quality_point_a: "",
    quality_point_b: "",
    quality_point_c: "",
    quality_point_d: "",
    record: "",
    reference: "",
    observation: "",
    part_a: null,
    part_b: null,
    part_c: null,
    part_d: null,
    validated: false,
    order: "",
    revision: 0,
    is_locked: false
  }
}

/**
 * Create new empty `NonConformity` object
 * @returns The new NonConformity
 */
function mkNonConformity() {
  return {
    area: "",
    detailed_description: "",
    due_to: undefined,
    event: "",
    inspection: null,
    other: "",
    pictures: [],
    quick_description: "",
    report_date: today(),
    root_cause_analysis: "",
    status: "Ongoing",
    criticality: "Minor",
    lang: "",
    nc_number: "",
    notify_oncreated: false,
    notify_team_oncreated: false,
    users: [],
    attachments: [],
    use5Why: true,
    useRca: false,
    whys: [{ id: 0, description: "", action: "", order: 1 }]
  }
}

/**
 * Create new empty `NonConformity` object
 * @returns The new NonConformity
 */
function mkCfsi() {
  return {
    non_conformity: null,
    cfsi_reference: "",
    pictures: [],
    quick_description: "",
    report_date: now(),
    status: "Ongoing",
    lang: "",
    notify_oncreated: false,
    notify_team_oncreated: false,
    users: []
  }
}

/**
 * Create new empty `InternalNotification` object
 * @returns The new InternalNotification
 */
function mkinternalNotification() {
  return {
    description: "",
    notification_object: "",
    project: null,
    attachments: [],
    users: [],
    external_emails: [],
    lang: ""
  }
}

/**
 * Create new empty `NonConformityAction` object
 * @returns The new NonConformityAction
 */
function mkNCAction(nonConformityId, count) {
  return {
    id: null,
    name: `ACTION-${count}`,
    description: null,
    end_date: tomorrow(),
    non_conformity: nonConformityId,
    type_action: null,
    status: null,
    user: null,
    nc_why: null,
    lang: "",
    is_nc_linked: true,
    monitoring_description: null,
    monitoring_user: null,
    monitoring_end_date: tomorrow(),
    monitoring_status: null
  }
}

/**
 * Create new empty `NonConformityAction` object
 * @returns The new NonConformityAction
 */
function mkCFSIAction(nonConformityId, count) {
  return {
    id: null,
    name: `ACTION-${count}`,
    description: null,
    end_date: tomorrow(),
    cfsi: nonConformityId,
    type_action: null,
    status: null,
    user: null,
    lang: ""
  }
}

/**
 * Create new empty `Invites` object
 * @returns The new invite
 */
function mkInvites() {
  return {
    email: "",
    errors: "",
    privileges: {
      inspector: false,
      operator: true
    }
  }
}

/**
 * Create new empty `Project` object
 * @param {*} company The Company object linked to project
 * @returns The new Project
 */
function mkProject(company, translate = "PROJECTNAME") {
  return {
    company: company,
    department: null,
    description: "",
    name: `${translate}`,
    start_date: today(),
    end_date: add(today(), 1, "year"),
    steps: [],
    picture: null,
    logo: null,
    show_pcq_dsi: false,
    lang: ""
    // validated_pcq_dsi: false
  }
}

/**
 * Create new empty `ProjectStep` object
 * @returns The new ProjectStep
 */
function mkProjectStep(start_date = null, order = null, translate = "ACTIVITY") {
  start_date = start_date ? copy(start_date) : today()
  let end_date = tomorrow(copy(start_date))
  return {
    deadline: end_date,
    name: `${translate}-${order}`,
    order: order,
    picture_data: null,
    start_date: start_date,
    tasks: [],
    inspections: []
  }
}

/**
 * Create new empty `ProjectStepTask` object
 * @returns The new ProjectStepTask
 */
function mkProjectStepTask(start_date = null, end_date = null, order = null, translate = "TASK") {
  start_date = start_date ? copy(start_date) : today()
  end_date = end_date ? copy(end_date) : tomorrow()
  return {
    deadline: end_date,
    name: `${translate}-${order}`,
    order: order,
    picture_data: null,
    start_date: start_date,
    status: "Ongoing",
    create_pcq_dsi: false,
    description: "",
    lang: "",
    as_detail: false,
    user: null,
    attachments: []
  }
}

/**
 * Create new empty `ProjectStepInspection` object
 * @returns The new ProjectStepInspection
 */
function mkProjectStepInspection(start_date = null, end_date = null, order = null) {
  start_date = start_date ? copy(start_date) : today()
  end_date = end_date ? copy(end_date) : tomorrow()
  return {
    end_date: end_date,
    name: `INSPECTION-${order}`,
    order: order,
    // picture_data: null,
    start_date: start_date,
    status: "Ongoing"
  }
}

/**
 * Create new empty `Template` object
 * @returns The new Template
 */
function mkTemplate() {
  return {
    name: "",
    state: STATE_ENUM.EDITABLE,
    type: null,
    company: null
  }
}

/**
 * Create new empty `PCQSignature` object
 * @returns The new PCQSignature
 */
function mkPCQAction(pcqId, count) {
  return {
    id: null,
    section: null,
    signature_image: null,
    sign_date: null,
    pcq_dsi: pcqId,
    external_email: null,
    assigned_user: null
  }
}

/**
 * Create new empty `PckLiner` object
 * @returns The new PckLiner
 */
function PckLiner() {
  return {
    name: "",
    description: "",
    revision: 0,
    is_locked: false
  }
}

export {
  injectValues,
  mkDocument,
  mkInspection,
  mkNCAction,
  mkInvites,
  mkNonConformity,
  mkProject,
  mkProjectStep,
  mkProjectStepTask,
  mkProjectStepInspection,
  mkTemplate,
  mkPcqDsi,
  mkinternalNotification,
  mkCfsi,
  mkCFSIAction,
  mkPCQAction,
  PckLiner
}
